import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { getLaunchRemaining } from "@/lib/launch";

const DISMISS_KEY = "m2i_exit_intent_dismissed";
// Only show after the visitor has been on the page long enough to form intent.
const MIN_DWELL_MS = 15_000;

export default function ExitIntentBar({ onBuy }) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [tick, setTick] = useState(() => getLaunchRemaining());

  // Live-update the countdown shown in the bar
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setTick(getLaunchRemaining()), 1000);
    return () => clearInterval(id);
  }, [visible]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, MIN_DWELL_MS);

    const trigger = () => {
      if (!armed) return;
      const r = getLaunchRemaining();
      if (r.ended) return;
      setTick(r);
      setVisible(true);
      cleanup();
    };

    const onMouseOut = (e) => {
      // Fires when the cursor leaves the viewport. Only count top-edge exits.
      if (e.relatedTarget || e.toElement) return;
      if (e.clientY <= 0) trigger();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") trigger();
    };

    document.addEventListener("mouseout", onMouseOut);
    document.addEventListener("visibilitychange", onVisibility);
    const cleanup = () => {
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(armTimer);
    };
    return cleanup;
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const handleBuy = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    onBuy?.();
  };

  const totalMinutes = Math.max(1, Math.ceil(tick.diff / 60_000));
  const mins = String(tick.m).padStart(2, "0");
  const secs = String(tick.s).padStart(2, "0");

  return (
    <AnimatePresence>
      {visible && !tick.ended && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          className="fixed inset-x-0 bottom-0 z-[90] px-3 pb-3 md:px-5 md:pb-5 pointer-events-none"
          data-testid="exit-intent-bar"
        >
          <div
            className="mx-auto max-w-[1200px] rounded-full bg-[var(--ink)] text-[var(--cream)] shadow-[0_20px_60px_-20px_rgba(15,31,23,0.6)] border border-[rgba(230,92,0,0.5)] pointer-events-auto"
            style={{ backdropFilter: "blur(12px)" }}
          >
            <div className="flex items-center gap-3 md:gap-5 py-3 pl-4 pr-3 md:pl-6 md:pr-4">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--marigold)] opacity-80" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--marigold)]" />
              </span>

              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
                <span className="font-display text-[0.95rem] md:text-[1.05rem] leading-tight tracking-tight truncate">
                  {t.exit_intent.headline_a}{" "}
                  <span className="font-editorial-italic text-[var(--marigold-2)]">
                    {totalMinutes} {totalMinutes === 1 ? t.exit_intent.minute : t.exit_intent.minutes}
                  </span>{" "}
                  {t.exit_intent.headline_b}
                </span>
                <span className="hidden md:inline-flex items-center gap-1.5 font-mono-caption text-[11px] tracking-[0.14em] text-[var(--cream)]/60">
                  <span className="w-1 h-1 rounded-full bg-[var(--cream)]/30" />
                  {mins}:{secs}
                </span>
              </div>

              <button
                onClick={handleBuy}
                className="btn-pill btn-primary text-[13px] md:text-[14px] py-2 px-4 md:px-5 shrink-0"
                data-testid="exit-intent-buy"
              >
                <span>{t.exit_intent.cta}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              <button
                onClick={dismiss}
                aria-label={t.exit_intent.dismiss}
                className="w-8 h-8 rounded-full border border-[rgba(253,251,247,0.2)] hover:bg-[rgba(253,251,247,0.08)] flex items-center justify-center shrink-0"
                data-testid="exit-intent-close"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
